import {
    Component,
    EnvironmentInjector,
    EnvironmentProviders, ErrorHandler, inject, InjectionToken, Injector, Provider, runInInjectionContext, Type, provideAppInitializer
} from "@angular/core";
import { APP_FEATURE_GROUP_ID } from "../constants/app-constants";
import {
    IMgdRoute, MgdRoutes
} from "../interfaces/route";
import {
    FeatureGroupNavCommandData, RouteNavCommandData
} from "../models/route-nav-command";
import {
    ArrayUtil, ObjectUtil
} from "@shopliftr/common-js/data";
import { IAppRouterService } from "../interfaces/app-router-service";
import { CommonAppRouteId } from "../enums/common-app-route-id";
import {
    APP_ERROR_NAV_COMMAND_DATA, APP_ROOT_NAV_COMMAND_DATA, APP_ROUTER_SERVICE, APP_LOGOUT_NAV_COMMAND_DATA,
    COMMON_APP_CONFIG,
    COMMON_APP_SERVICE
} from "../constants/app-tokens";
import {
    CanActivateFn, CanActivateChildFn, CanDeactivateFn, provideRouter,
    GuardResult,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    withInMemoryScrolling
} from "@angular/router";
import { MgdRouterService } from "../services/mgd-router.service";
import { AuthenticationRouteId } from "../../iapi-application/enums/authentication-route-id";
import { ICommonConfig } from "../interfaces/common-config";
import { COMMON_APP_ROUTES } from "../common-app.routes";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
import { CommonErrorHandlerService } from "../../exceptions/services/common-error-handler.service";
import { CommonHttpInterceptor } from "../../interceptors/common-http-interceptor";
import { IAPI_ROUTES } from "../../iapi-application/iapi-app.routes";
import { IAppOptions } from "../interfaces/app-options";
import { IInternalApiConfig } from "../../iapi-application/interfaces/internal-api-config";
import { InternalApiInterceptor } from "../../iapi-application/interceptors/internal-api-interceptor";
import { SidenavComponent } from "../../iapi-application/views/sidenav/sidenav.component";
import { authenticationCanActivate } from "../../iapi-application/guards/authentication-can-activate.guard";
import {
    IAPI_APP_ROUTER_SERVICE, INTERNAL_API_APP_CONFIG
} from "../../iapi-application/constants/iapi-app-tokens";
import {
    HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi
} from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { IAppService } from "../interfaces/app-service";
import { IFeatureGroupOptions } from "../interfaces/feature-group-options";
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from "@angular/material/snack-bar";
import {
    provideLuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS
} from "@angular/material-luxon-adapter";
import { RxjsUtil } from "../../utils/rxjs-util";
import {
    Observable, of, switchMap
} from "rxjs";
import { localizationUtilInitializer } from "../../iapi-application/initializers/localization-util-initializer";

/**
 * Utility class for configuring routes in route files and providing them
 * in the providersForApplication function used when bootstrapping the application
 *
 * Used by the RouterServes to get various injection tokens for feature group ids
 */
export class AppUtil {

    /**
     * The providers built by {@link AppUtil.configureApplication}, for passing to
     * `bootstrapApplication`. Empty until configureApplication has run.
     */
    static get appProviders(): Array<Provider | EnvironmentProviders> {

        return this._appProviders;
    }

    static get materialLuxonProviders(): Array<Provider> {

        return [
            provideLuxonDateAdapter(),
            {
                provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
                useValue: {
                    useUtc: false,
                    // Force the first day of the week in the calendar to be Sunday
                    firstDayOfWeek: 7,
                    defaultOutputCalendar: "gregory"
                }
            }
        ];
    }

    /**
     * Nav command data by feature group id
     *
     * Used by getNavCommandData to retrieve nav command data for a feature group
     */
    private static readonly _NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID = new Map<string, FeatureGroupNavCommandData>();

    /**
     * Parent nav command data by feature group id
     *
     * Used by getParentNavCommandData to retrieve parent nav command data for a feature group
     */
    private static readonly _PARENT_NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID = new Map<string, RouteNavCommandData>();

    /**
     * NavCommandData for the APP routes by routeId
     *
     * Populated by the configureAppRoutes and configureSideLoadedAppRoutes functions
     *
     * And provided in the getAppProviders function
     */
    private static readonly _APP_NAV_COMMAND_DATA = new Map<string, RouteNavCommandData>();

    /**
     * In addition to providing the AppRouterService by its class, these injectors will also provide the same instance of the service
     *
     * Populated by the configureAppRoutes and configureSideLoadedAppRoutes functions
     *
     * And provided in the getAppProviders function
     */
    private static readonly _APP_ROUTER_SERVICE_TOKENS = new Array<InjectionToken<IAppRouterService>>();

    /**
     * The app providers for the given app
     */
    private static _appProviders: Array<Provider | EnvironmentProviders> = [];

    /**
     * Resets the app util back to its initial state
     */
    static reset(): void {
        this._APP_NAV_COMMAND_DATA.clear();
        this._NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.clear();
        this._PARENT_NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.clear();
        this._APP_ROUTER_SERVICE_TOKENS.length = 0;
        this._appProviders = [];
    };

    /**
     * Combines multiple CanActivate guards into a single guard that executes them sequentially.
     * Guards are processed in order, and execution stops at the first guard that returns false, UrlTree, or RedirectCommand.
     *
     * @param guards - Array of CanActivate guard functions to combine
     * @returns A single CanActivate guard function that processes all guards sequentially
     *
     * @example
     * ```typescript
     * // Combine authentication and authorization guards
     * const combinedGuard = AppUtil.combineCanActivateGuard(
     *   authenticationGuard,
     *   authorizationGuard,
     *   featureToggleGuard
     * );
     *
     * // Use in route configuration
     * const routes: Routes = [
     *   {
     *     path: 'protected',
     *     component: ProtectedComponent,
     *     canActivate: [combinedGuard]
     *   }
     * ];
     * ```
     *
     * @example
     * ```typescript
     * // Guards can return sync or async results
     * const syncGuard: CanActivateFn = () => true;
     * const asyncGuard: CanActivateFn = () => Promise.resolve(true);
     * const observableGuard: CanActivateFn = () => of(true);
     *
     * const combined = AppUtil.combineCanActivateGuard(syncGuard, asyncGuard, observableGuard);
     * ```
     */
    static combineCanActivateGuard(...guards: Array<CanActivateFn>): CanActivateFn {
        return (route, state) => {
            const injector = inject(EnvironmentInjector);
            return this._processGuard$(injector, guards, 0, route, state);
        };
    }

    /**
     * Combines multiple CanActivateChild guards into a single guard that executes them sequentially.
     * Guards are processed in order, and execution stops at the first guard that returns false, UrlTree, or RedirectCommand.
     *
     * @param guards - Array of CanActivateChild guard functions to combine
     * @returns A single CanActivateChild guard function that processes all guards sequentially
     *
     * @example
     * ```typescript
     * // Combine child route guards
     * const combinedGuard = AppUtil.combineCanActivateChildGuard(
     *   childAuthGuard,
     *   childPermissionGuard
     * );
     *
     * // Use in route configuration
     * const routes: Routes = [
     *   {
     *     path: 'parent',
     *     component: ParentComponent,
     *     canActivateChild: [combinedGuard],
     *     children: [...]
     *   }
     * ];
     * ```
     */
    static combineCanActivateChildGuard(...guards: Array<CanActivateChildFn>): CanActivateChildFn {
        return (route, state) => {
            const injector = inject(EnvironmentInjector);
            return this._processGuard$(injector, guards, 0, route, state);
        };
    }

    /**
     * Combines multiple CanDeactivate guards into a single guard that executes them sequentially.
     * Guards are processed in order, and execution stops at the first guard that returns false, UrlTree, or RedirectCommand.
     *
     * @param guards - Array of CanDeactivate guard functions to combine
     * @returns A single CanDeactivate guard function that processes all guards sequentially
     *
     * @example
     * ```typescript
     * // Combine deactivation guards
     * const combinedGuard = AppUtil.combineCanDeactivateGuard(
     *   unsavedChangesGuard,
     *   confirmExitGuard
     * );
     *
     * // Use in route configuration
     * const routes: Routes = [
     *   {
     *     path: 'edit',
     *     component: EditComponent,
     *     canDeactivate: [combinedGuard]
     *   }
     * ];
     * ```
     */
    static combineCanDeactivateGuard<T = unknown>(...guards: Array<CanDeactivateFn<T>>): CanDeactivateFn<T> {
        return (component, route, state, nextState) => {
            const injector = inject(EnvironmentInjector);
            return this._processDeactivateGuard$(injector, guards, 0, component, route, state, nextState);
        };
    }

    /**
     * Returns the nav command data for a feature group
     *
     * Used by RouterServices to determine Router commands for route ids
     */
    static getNavCommandData(featureGroupId: string): FeatureGroupNavCommandData {
        if (!featureGroupId) {
            throw new Error("Feature group id is required");
        }
        return this._NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.get(featureGroupId);
    }

    /**
     * Returns the parent route nav command data for a feature group
     *
     * The data provides the route nav command currently used to load feature group
     *
     * Used by RouterServices to determine Router commands for route ids
     */
    static getParentNavCommandData(featureGroupId: string): RouteNavCommandData | undefined {
        if (!featureGroupId) {
            throw new Error("Feature group id is required");
        }
        return this._PARENT_NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.get(featureGroupId);
    }

    /**
     * Configures a feature group with routes, guards, and providers.
     *
     * Creates a parent route wrapper that provides navigation command data
     * for all child routes. Automatically adds the featureGroupId to each route's data object.
     *
     * Supports optional guards (canActivate, canActivateChild, canDeactivate, canMatch) that will
     * be applied to the entire feature group, and resolve data for pre-loading.
     *
     * @param options - Configuration options for the feature group
     * @returns Array containing the configured root route with all child routes
     *
     * @example
     * ```typescript
     * const featureRoutes = AppUtil.configureFeatureGroup({
     *   featureGroupId: 'user-management',
     *   routes: userRoutes,
     *   canActivate: [authGuard, permissionGuard],
     *   additionalProviders: [UserRequestService]
     * });
     * ```
     */
    static configureFeatureGroup(options: IFeatureGroupOptions): MgdRoutes {

        if (!options?.featureGroupId) {
            throw new Error("Router service id is required");
        }
        const navCommandData = new Map<string, RouteNavCommandData>();
        // create a parent route for the routes in order to provide the router service and the FeatureGroupNaveCommandData
        const rootRoute: IMgdRoute = {
            path: "",
            children: options.routes,
            providers: []
        };

        if (options.additionalProviders) {
            rootRoute.providers.push(...options.additionalProviders);
        }

        if (options.canActivate) {
            rootRoute.canActivate = options.canActivate;
        }

        if (options.canActivateChild) {
            rootRoute.canActivateChild = options.canActivateChild;
        }

        if (options.canDeactivate) {
            rootRoute.canDeactivate = options.canDeactivate;
        }

        if (options.canMatch) {
            rootRoute.canMatch = options.canMatch;
        }

        if (options.resolve) {
            rootRoute.resolve = options.resolve;
        }

        // paths relative to the feature group
        // used when processing the routes
        // the path will start empty
        const paths = new Array<string>();
        // Process the parent route before processing the child routes
        this._processRoute(rootRoute, options.featureGroupId, paths, navCommandData);
        this._processRoutes(options.routes, options.featureGroupId, paths, navCommandData);

        this._NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.set(options.featureGroupId, navCommandData);

        return [rootRoute];
    }

    /**
     * Configure additional app routes for the application
     *
     * This is used to load common additional routes in the applications i.e. Authentication Routes
     * Most commonly used on common-ng to provide for optional routes
     *
     * Cannot define Root or Error routes
     *
     * if additionAppRouterServiceToken is defined it will be provided in get
     */
    static configureSideLoadedAppRoutes(
        routes: MgdRoutes, additionalAppRouterServiceToken?: InjectionToken<IAppRouterService>
    ): MgdRoutes {
        const workingRoutes: MgdRoutes = ObjectUtil.clone(routes);
        if (additionalAppRouterServiceToken) {
            this._APP_ROUTER_SERVICE_TOKENS.push(additionalAppRouterServiceToken);
        }
        // used when processing the routes
        // the path will start empty
        const paths = new Array<string>();
        this._processRoutes(workingRoutes, APP_FEATURE_GROUP_ID, paths, this._APP_NAV_COMMAND_DATA, true);
        return workingRoutes;
    }

    /**
     * Configures the complete Angular application with routing, services, and providers.
     *
     * This is the primary configuration method that sets up all necessary providers for the application,
     * including routing, HTTP interceptors, error handling, Material Design components, and optional
     * Internal API integration.
     *
     * @param options - Configuration options for the application
     * @param options.config - Application configuration object (ICommonConfig or IInternalApiConfig)
     * @param options.configInjectionToken - Injection token for the configuration object
     * @param options.routes - Array of application routes to configure
     * @param options.appRouterService - Service class that implements IAppRouterService for navigation
     * @param options.AppService - Service class that implements IAppService for app state management
     * @param options.useInternalApi - Whether to enable Internal API integration (default: true)
     * @param options.rootViewComponent - Root component when useInternalApi is false (required if useInternalApi is false)
     * @param options.rootBreadcrumb - Optional breadcrumb text for the root route
     * @param options.useResources - Whether to enable resource loading resolver (requires useInternalApi: true)
     * @param options.includeAnimations - Whether to include Angular animations (default: true)
     * @param options.additionalProviders - Optional additional providers to include
     *
     * @throws {Error} When required options are missing or invalid
     * @throws {Error} When useInternalApi is false but rootViewComponent is not provided
     * @throws {Error} When useResources is true but useInternalApi is false
     * @throws {Error} When Internal API config is missing required authentication or apiUrl properties
     *
     * @example
     * ```typescript
     * // Basic application configuration with Internal API
     * AppUtil.configureApplication({
     *   config: myInternalApiConfig,
     *   configInjectionToken: MY_CONFIG_TOKEN,
     *   routes: appRoutes,
     *   appRouterService: MyAppRouterService,
     *   AppService: MyAppService
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Application without Internal API
     * AppUtil.configureApplication({
     *   config: myCommonConfig,
     *   configInjectionToken: MY_CONFIG_TOKEN,
     *   routes: appRoutes,
     *   appRouterService: MyAppRouterService,
     *   AppService: MyAppService,
     *   useInternalApi: false,
     *   rootViewComponent: MyRootComponent
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Application with resource loading and custom providers
     * AppUtil.configureApplication({
     *   config: myInternalApiConfig,
     *   configInjectionToken: MY_CONFIG_TOKEN,
     *   routes: appRoutes,
     *   appRouterService: MyAppRouterService,
     *   AppService: MyAppService,
     *   useResources: true,
     *   rootBreadcrumb: 'Home',
     *   additionalProviders: [MyCustomService, { provide: MY_TOKEN, useValue: 'value' }]
     * });
     * ```
     */
    static configureApplication<C extends ICommonConfig | IInternalApiConfig>(options: IAppOptions<C>): void {

        if (!options) {
            throw new Error("options is required");
        }

        if (!options.config || !options.configInjectionToken) {
            throw new Error("config and configInjectionToken are both required options");
        }

        if (ArrayUtil.isEmpty(options.routes)) {
            throw new Error("routes is required");
        }

        if (!options.appRouterService) {
            throw new Error("appRouterService is required");
        }

        if (!options.appService) {
            throw new Error("AppService is required");
        }

        const useInternalApi = options.useInternalApi ?? true;

        if (!useInternalApi && !options.rootViewComponent) {
            throw new Error("Must define rootViewComponent when useInternalApi is false");
        }

        if (options.useLocalizations && !useInternalApi) {
            throw new Error("Cannot useResources when useInternalApi is false");
        }

        this.reset();

        this._addAppConfigProviders(options);
        this._addAppStateProviders(options.appService);
        this._addHttpProviders(useInternalApi);
        this._addErrorHandlerProviders();
        this._addMaterialProviders();

        if (options.includeAnimations ?? true) {
            // Add angular animation support
            this._appProviders.push(provideAnimations());
        }

        // Start building the app routes, with the routes common to all apps
        AppUtil._addRouteProviders(
            useInternalApi,
            options.routes,
            options.appRouterService,
            options.rootViewComponent,
            options.rootBreadcrumb
        );

        if (options.useLocalizations) {
            // Add resource loading resolver
            this._appProviders.push(provideAppInitializer(localizationUtilInitializer));
        }
        if (options.additionalProviders) {
            this._appProviders.push(...options.additionalProviders);
        }
    };

    /**
     * Adds Route providers to the appProviders
     */
    private static _addRouteProviders(
        useInternalApi: boolean,
        childRoutes: MgdRoutes,
        appRouterService: Type<IAppRouterService>,
        rootViewComponent?: Type<Component>,
        rootBreadcrumb?: string
    ): void {

        let routes = [...AppUtil.configureSideLoadedAppRoutes(COMMON_APP_ROUTES)];

        // The route route is the primary route wrapper for the Main Application routes
        const rootRoute: IMgdRoute = {
            path: "",
            children: childRoutes,
            component: rootViewComponent ?? SidenavComponent,
            data: {
                breadcrumb: rootBreadcrumb,
                routeId: CommonAppRouteId.Root
            }
        };

        const errorRouteData = this._APP_NAV_COMMAND_DATA.get(CommonAppRouteId.Error);
        if (!errorRouteData) {
            throw new Error("Could not find errorRoute");
        }

        // Nav commands providers are used by the MgdRouterService to find specific routes
        const navCommandProviders: Array<Provider | EnvironmentProviders> = [{
            provide: APP_ERROR_NAV_COMMAND_DATA,
            useValue: errorRouteData
        }];

        if (useInternalApi) {
            // Authentication Routes and the interceptor are only needed if the internal API is enabled
            routes = [...this.configureSideLoadedAppRoutes(IAPI_ROUTES, IAPI_APP_ROUTER_SERVICE), ...routes];
            // When internal api is used, the root route handles authentication and displays the side nav
            rootRoute.canActivate = [authenticationCanActivate];
            const logoutRouteData = this._APP_NAV_COMMAND_DATA.get(AuthenticationRouteId.Logout);
            if (!logoutRouteData) {
                throw new Error("Could not find logout route");
            }

            navCommandProviders.push({
                provide: APP_LOGOUT_NAV_COMMAND_DATA,
                useValue: logoutRouteData
            });
        }

        routes = [...this._configureRootRoute(rootRoute), ...routes];

        const rootRouteData = this._APP_NAV_COMMAND_DATA.get(CommonAppRouteId.Root);
        navCommandProviders.push({
            provide: APP_ROOT_NAV_COMMAND_DATA,
            useValue: rootRouteData
        });

        this._appProviders.push(
            // Provide the processed routes and the Nav Command providers
            provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: "enabled" })),
            ...navCommandProviders,
            // Provide the MgdRouterService
            MgdRouterService,
            // Provide the router service
            appRouterService,
            // Add additional tokens for the app router service for common-ng artifacts to use
            {
                provide: APP_ROUTER_SERVICE,
                useExisting: appRouterService
            },
            ...this._APP_ROUTER_SERVICE_TOKENS.map((injector) => {
                // Provide additional injectors for the router service
                return {
                    provide: injector,
                    useExisting: appRouterService
                };
            })
        );

        this._NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.set(APP_FEATURE_GROUP_ID, this._APP_NAV_COMMAND_DATA);
    }

    /**
     * Adds the providers for the app state service
     */
    private static _addAppStateProviders(appService: Type<IAppService>): void {

        this._appProviders.push(
            appService,
            {
                provide: COMMON_APP_SERVICE,
                useExisting: appService
            }
        );
    }

    /**
     * Adds the providers for for error handling
     */
    private static _addErrorHandlerProviders(): void {

        this._appProviders.push(
            {
                provide: COMMON_ERROR_HANDLER,
                useExisting: CommonErrorHandlerService
            },
            {
                provide: ErrorHandler,
                useExisting: COMMON_ERROR_HANDLER
            }
        );
    }

    /**
     * Adds the providers for http transactions to appProviders
     *
     * if useInternalApi is true it will include the InternalApiInterceptor
     */
    private static _addHttpProviders(useInternalApi: boolean): void {

        this._appProviders.push(
            // Standard Http Providers
            {
                provide: HTTP_INTERCEPTORS,
                useClass: CommonHttpInterceptor,
                multi: true
            },
            provideHttpClient(withInterceptorsFromDi())
        );

        if (useInternalApi) {
            this._appProviders.push(
                // The internal api interceptor is only used when internal api is required
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: InternalApiInterceptor,
                    multi: true
                }
            );
        }
    }

    /**
     * Adds the configuration providers for material
     */
    private static _addMaterialProviders(): void {

        this._appProviders.push({
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                horizontalPosition: "right",
                verticalPosition: "top"
            }
        });

        this._appProviders.push(
            ...this.materialLuxonProviders
        );
    }

    /**
     * Configure the root route for the application
     */
    private static _configureRootRoute(route: IMgdRoute): MgdRoutes {

        // Add the APP_ROUTER_SERVE injection token
        this._APP_ROUTER_SERVICE_TOKENS.push(APP_ROUTER_SERVICE);
        // used when processing the routes
        // the path will start empty
        const paths = new Array<string>();
        const routes = [route];
        this._processRoutes(routes, APP_FEATURE_GROUP_ID, paths, this._APP_NAV_COMMAND_DATA);
        return routes;
    }

    /**
     * Validates a route
     *
     * Throws an error if the route is invalid
     */
    private static _validateRoute(route: IMgdRoute, groupNavCommandData: Map<string, RouteNavCommandData>): void {

        const hasComponent = route.component || route.loadComponent;
        const hasChildren = !ArrayUtil.isEmpty(route.children) || route.loadChildren;
        const featureGroupId = route.data?.featureGroupId;
        const routeId = route.data?.routeId;
        if (!featureGroupId) {
            throw new Error("featureGroupId was not set by RouterUtil");
        }
        if (routeId && groupNavCommandData.has(routeId)) {
            throw new Error(`The routeId ${routeId} is used more than once in feature group ${featureGroupId}`);
        }
        if (hasComponent) {
            if (!routeId && route.path !== "**") {
                throw new Error(`Route at ${route.path} must have a routeId`);
            }
        }

        if (route.loadChildren) {
            if (!route.data?.childFeatureGroupId) {
                throw new Error("Routes that lazy-load children must have a childFeatureGroupId defined for the childRoutes");
            }
            if (!routeId) {
                throw new Error("Routes that lazy-load children must have a routeId");
            }
        }

        if (route.data?.breadcrumb && !hasChildren && !hasComponent) {
            throw new Error("Route with a breadcrumb must have a component or a child");
        }
    }

    /**
     * Returns true if the routes contain a default route which has an empty path
     * This ensures that when navigating to these child routes that no additional path params are required
     * Often this will be a simple re-direct to another route
     */
    private static _hasDefaultRoute(routes: MgdRoutes): boolean {

        return routes.some((route) => this._isDefault(route));
    }

    /**
     * Returns true if the path is empty
     */
    private static _hasEmptyPath(route: IMgdRoute): boolean {

        return route.path === "";
    }

    /**
     * Returns true if the route is a greedy route and will apply to anything
     */
    private static _hasGreedyPath(route: IMgdRoute): boolean {

        return route.path === "**";
    }

    private static _isDefault(route: IMgdRoute): boolean {

        return this._hasEmptyPath(route) || this._hasGreedyPath(route);
    }

    /**
     * Adds required data to the route
     * If the route is not valid it will throw and exception
     * If the route has a routeId it will be added to the groupNavCommandData
     * If the route lazy loads another feature group it will provide the ParentNavCommand for the childFeatureGroupId
     */
    private static _processRoute(
        route: IMgdRoute, featureGroupId: string, paths: Array<string>, groupNavCommandData: Map<string, RouteNavCommandData>
    ): IMgdRoute {

        route.data = {
            ...route.data ?? {},
            featureGroupId: featureGroupId
        };
        this._validateRoute(route, groupNavCommandData);
        if (route.data.routeId) {

            const navCommandData = new RouteNavCommandData(route.data.routeId, featureGroupId, paths, route.data.sidenavLink);
            groupNavCommandData.set(route.data.routeId, navCommandData);
            if (route.loadChildren) {
                this._PARENT_NAV_COMMAND_DATA_BY_FEATURE_GROUP_ID.set(
                    route.data.childFeatureGroupId, navCommandData
                );
            }
        }
        return route;
    }

    /**
     * Validates and processes an array of routes by appending the featureGroupId to each route
     *
     * It will update the groupNavCommandData where appropriate
     *
     * It will recursively process and child routes.
     *
     * Unless skipDefaultCheck is provided it will ensure that there is a default route for this array of routes
     */
    private static _processRoutes(
        routes: MgdRoutes,
        featureGroupId: string,
        paths: Array<string>,
        groupNavCommandData: Map<string, RouteNavCommandData>,
        skipDefaultCheck?: boolean
    ): void {

        if (!skipDefaultCheck && !this._hasDefaultRoute(routes)) {
            throw new Error("There must be a default route with an empty path");
        }
        routes.forEach((route) => {
            const routePaths = [...paths];
            if (!this._isDefault(route)) {
                routePaths.push(route.path);
            }
            this._processRoute(route, featureGroupId, routePaths, groupNavCommandData);
            if (route.children?.length) {
                this._processRoutes(route.children, featureGroupId, routePaths, groupNavCommandData);
            }
        });
    }

    /**
     * Provides the common application configuration providers
     * @param options The configuration options
     * @param options.config The application configuration
     * @param options.configInjectionToken The injection token for the application configuration
     * @param options.excludeInternalApi Whether to exclude the internal API integration for this application
     */
    private static _addAppConfigProviders<C extends ICommonConfig | IInternalApiConfig>(options: IAppOptions<C>): void {

        this._appProviders.push(
            {
                provide: options.configInjectionToken,
                useValue: options.config
            },
            {
                provide: COMMON_APP_CONFIG,
                useExisting: options.configInjectionToken
            }
        );

        if (options.useInternalApi ?? true) {
            const config = options.config as IInternalApiConfig;
            if (!config.authentication) {
                throw new Error("Config is missing authentication options");
            }
            if (!config.apiUrl) {
                throw new Error("Config is missing apiUrl");
            }

            this._appProviders.push({
                provide: INTERNAL_API_APP_CONFIG,
                useExisting: options.configInjectionToken
            });
        }
    };

    /**
     * Processes guards sequentially, handling both synchronous and asynchronous results.
     *
     * @param guards - Array of guard functions to process
     * @param currentIndex - Current index in the guards array
     * @param route - The activated route snapshot
     * @param state - The router state snapshot
     * @returns Observable that emits the guard result (true to continue, false/UrlTree/RedirectCommand to stop)
     *
     * @private
     */
    private static _processGuard$(
        injector: Injector,
        guards: Array<CanActivateFn> | Array<CanActivateChildFn>,
        currentIndex: number,
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<GuardResult> {
        if (currentIndex >= guards.length) {
            return of(true);
        }

        const guard = guards[currentIndex];
        const result$ = runInInjectionContext(injector, () => guard(route, state));

        return RxjsUtil.handleMaybeAsync$(result$).pipe(
            switchMap((canActivate) => {
                if (canActivate === true) {
                    return this._processGuard$(injector, guards, currentIndex + 1, route, state);
                }
                return of(canActivate);
            })
        );
    }

    /**
     * Processes CanDeactivate guards sequentially, handling both synchronous and asynchronous results.
     *
     * @param guards - Array of CanDeactivate guard functions to process
     * @param currentIndex - Current index in the guards array
     * @param component - The component instance
     * @param route - The activated route snapshot
     * @param state - The current router state snapshot
     * @param nextState - The next router state snapshot
     * @returns Observable that emits the guard result (true to continue, false/UrlTree/RedirectCommand to stop)
     *
     * @private
     */
    private static _processDeactivateGuard$<T>(
        injector: Injector,
        guards: Array<CanDeactivateFn<T>>,
        currentIndex: number,
        component: T,
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
        nextState: RouterStateSnapshot
    ): Observable<GuardResult> {
        if (currentIndex >= guards.length) {
            return of(true);
        }

        const guard = guards[currentIndex];
        const result$ = runInInjectionContext(injector, () => guard(component, route, state, nextState));

        return RxjsUtil.handleMaybeAsync$(result$).pipe(
            switchMap((canDeactivate) => {
                if (canDeactivate === true) {
                    return this._processDeactivateGuard$(injector, guards, currentIndex + 1, component, route, state, nextState);
                }
                return of(canDeactivate);
            })
        );
    }

}
